import { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './Formulario.module.css';
import Swal from 'sweetalert2';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {useAuth} from '../../Auth/AuthContext'; 

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/universidades`;

const EditarPlantel = ({ abrirModal, cerrarModal, plantelId, onPlantelEditado }) => {
    const {getToken} = useAuth(); 
    const token = getToken(); 
    const [formData, setFormData] = useState({
        direccion: '',
        telefonos: [''],
        correos: [''],
        locacion: '',
    });
    const [isClosing, setIsClosing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errores, setErrores] = useState({
        direccion: '',
        correos: [],
        telefonos: [],
        locacion: '',
    });

    const extractGoogleMapsUrl = useCallback((input) => {
    if (!input || typeof input !== 'string') return input;
    
    // Buscar el patrón src="URL" en el iframe
    const srcMatch = input.match(/src=["']([^"']+)["']/i);
    if (srcMatch && srcMatch[1]) {
        return srcMatch[1];
    }
    
    // Si no es un iframe, devolver el input original
    return input;
    }, []);

    const validators = useMemo(() => ({
        email: (email) => {
            if (!email) return '';
            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            return !emailRegex.test(email) ? 'El correo no es válido.' : '';
        },
        
        telefono: (telefono) => {
            if (!telefono) return '';
            return (telefono.length < 10 || telefono.length > 15) 
                ? 'El número de teléfono debe tener entre 10 y 15 dígitos.' 
                : '';
        },
        
        locacion: (embedUrl) => {
            if (!embedUrl) return '';
            const url = extractGoogleMapsUrl(embedUrl);
            return (!url.includes('google.com/maps/embed') && !url.includes('maps.google.com/maps'))
                ? 'Debe ser una URL de Google Maps embed válida.'
                : '';
        },

        // Nueva validación para dirección
        direccion: (direccion) => {
            if (!direccion || !direccion.trim()) {
                return 'La dirección del plantel es obligatoria.';
            }
            
            const direccionLimpia = direccion.trim();
            
            // Validar longitud mínima
            if (direccionLimpia.length < 10) {
                return 'La dirección debe tener al menos 10 caracteres.';
            }
            
            // Validar longitud máxima
            if (direccionLimpia.length > 200) {
                return 'La dirección no puede exceder 200 caracteres.';
            }
            
            // Validar que contenga al menos una letra y un número (formato típico de dirección)
            const tieneLetras = /[a-zA-ZáéíóúÁÉÍÓÚñÑ]/.test(direccionLimpia);
            const tieneNumeros = /\d/.test(direccionLimpia);
            
            if (!tieneLetras) {
                return 'La dirección debe contener al menos una letra.';
            }
            
            if (!tieneNumeros) {
                return 'La dirección debe contener al menos un número.';
            }
            
            // Validar que no sea solo espacios, números o caracteres especiales
            const soloEspaciosYEspeciales = /^[\s,.\-#°0-9]+$/.test(direccionLimpia);
            if (soloEspaciosYEspeciales) {
                return 'La dirección debe contener texto descriptivo.';
            }
            
            return '';
        }
    }), [extractGoogleMapsUrl]);

    const parseArrayFromDB = useCallback((value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;

        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return parsed.filter(item => item && item.trim() !== '');
            } else {
                return [String(parsed)];
            }
        } catch (e) {
            if (typeof value === 'string') {
                if (value.includes(',')) {
                    return value.split(',').map(item => item.trim()).filter(item => item !== '');
                } else {
                    const trimmed = value.trim();
                    return trimmed ? [trimmed] : [];
                }
            } else {
                return [];
            }
        }
    }, []);

    const limpiarFormulario = useCallback(() => {
        setFormData({
            direccion: '',
            telefonos: [''],
            correos: [''],
            locacion: '',
        });
        setErrores({
            direccion: '',
            correos: [],
            telefonos: [],
            locacion: '',
        });
    }, []);


    const realizarPeticion = useCallback(async (url, options = {}) => {
        try {
            const response = await fetch(url, options);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            console.error('Error en petición:', error);
            throw error;
        }
    }, []);

    useEffect(() => {
        if (!plantelId || !abrirModal) return;

        const cargarDatosPlantel = async () => {
            setLoading(true);
            try {
                const data = await realizarPeticion(`${process.env.REACT_APP_API_URL}/admin/obtener-plantel/${plantelId}`, { 
                    headers: { 
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                setFormData({
                    direccion: data.plantel?.pl_direccion || '',
                    telefonos: parseArrayFromDB(data.plantel?.pl_numero).length > 0 ? 
                        parseArrayFromDB(data.plantel?.pl_numero) : [''],
                    correos: parseArrayFromDB(data.plantel?.pl_correo).length > 0 ? 
                        parseArrayFromDB(data.plantel?.pl_correo) : [''],
                    locacion: data.plantel?.pl_locacion || '',
                });
                
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudieron cargar los datos del plantel.',
                    icon: 'error',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57',
                    customClass: { popup: 'popupbonito' }
                });
            } finally {
                setLoading(false);
            }
        };

        cargarDatosPlantel();
    }, [plantelId, abrirModal, realizarPeticion, parseArrayFromDB]);

    useEffect(() => {
        if (!abrirModal && !isClosing) {
            limpiarFormulario();
        }
    }, [abrirModal, isClosing, limpiarFormulario]);

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                cerrarModal();
                setIsClosing(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing, cerrarModal]);

    const handleInputChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Validar en tiempo real según el campo
        if (field === 'locacion') {
            setErrores(prev => ({ ...prev, locacion: validators.locacion(value) }));
        } else if (field === 'direccion') {
            setErrores(prev => ({ ...prev, direccion: validators.direccion(value) }));
        }
    }, [validators]);

    // Nuevo manejador específico para dirección
    const handleDireccionChange = useCallback((e) => {
        const value = e.target.value;
        handleInputChange('direccion', value);
    }, [handleInputChange]);

    const handleLocacionChange = useCallback((e) => {
    let value = e.target.value;
    
    value = extractGoogleMapsUrl(value);
    
    handleInputChange('locacion', value);
    }, [handleInputChange, extractGoogleMapsUrl]);

    const handleTelefonoChange = useCallback((index, value) => {
        const nuevosTelefonos = [...formData.telefonos];
        nuevosTelefonos[index] = value;
        setFormData(prev => ({ ...prev, telefonos: nuevosTelefonos }));

        const nuevosErroresTelefonos = [...errores.telefonos];
        nuevosErroresTelefonos[index] = validators.telefono(value);
        setErrores(prev => ({ ...prev, telefonos: nuevosErroresTelefonos }));
    }, [formData.telefonos, errores.telefonos, validators]);

    const agregarTelefono = useCallback(() => {
        setFormData(prev => ({ ...prev, telefonos: [...prev.telefonos, ''] }));
        setErrores(prev => ({ ...prev, telefonos: [...prev.telefonos, ''] }));
    }, []);

    const eliminarTelefono = useCallback((index) => {
        if (formData.telefonos.length > 1) {
            setFormData(prev => ({ 
                ...prev, 
                telefonos: prev.telefonos.filter((_, i) => i !== index) 
            }));
            setErrores(prev => ({
                ...prev, 
                telefonos: prev.telefonos.filter((_, i) => i !== index)
            }));
        }
    }, [formData.telefonos.length]);

    const handleCorreoChange = useCallback((index, valor) => {
        const nuevosCorreos = [...formData.correos];
        nuevosCorreos[index] = valor;
        setFormData(prev => ({ ...prev, correos: nuevosCorreos }));

        const nuevosErroresCorreos = [...errores.correos];
        nuevosErroresCorreos[index] = validators.email(valor);
        setErrores(prev => ({ ...prev, correos: nuevosErroresCorreos }));
    }, [formData.correos, errores.correos, validators]);

    const agregarCorreo = useCallback(() => {
        setFormData(prev => ({ ...prev, correos: [...prev.correos, ''] }));
        setErrores(prev => ({ ...prev, correos: [...prev.correos, ''] }));
    }, []);

    const eliminarCorreo = useCallback((index) => {
        if (formData.correos.length > 1) {
            setFormData(prev => ({ 
                ...prev, 
                correos: prev.correos.filter((_, i) => i !== index) 
            }));
            setErrores(prev => ({
                ...prev,
                correos: prev.correos.filter((_, i) => i !== index)
            }));
        }
    }, [formData.correos.length]);

    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            setIsClosing(true);
        }
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsClosing(true);
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        // Validación completa del formulario
        const nuevosErrores = { 
            direccion: validators.direccion(formData.direccion),
            correos: [], 
            telefonos: [], 
            locacion: '' 
        };
        let hayErrores = false;

        // Validar dirección (ahora obligatoria)
        if (nuevosErrores.direccion) {
            hayErrores = true;
        }

        // Validar correos
        formData.correos.forEach((correo, index) => {
            if (correo.trim() && validators.email(correo)) {
                nuevosErrores.correos[index] = validators.email(correo);
                hayErrores = true;
            }
        });

        // Validar teléfonos
        formData.telefonos.forEach((telefono, index) => {
            if (telefono.trim() && validators.telefono(telefono)) {
                nuevosErrores.telefonos[index] = validators.telefono(telefono);
                hayErrores = true;
            }
        });

        // Validar locación
        if (formData.locacion.trim() && validators.locacion(formData.locacion)) {
            nuevosErrores.locacion = validators.locacion(formData.locacion);
            hayErrores = true;
        }

        setErrores(nuevosErrores);

        if (hayErrores) return;

        try {
            const telefonosFiltrados = formData.telefonos
                .filter(tel => tel && tel.trim() !== '')
                .map(tel => tel.replace(/\D/g, ''));
            const correosFiltrados = formData.correos.filter(correo => correo && correo.trim() !== '');

            const formDataToSend = new FormData();
            // La dirección ahora es obligatoria
            formDataToSend.append('direccion', formData.direccion.trim());
            if (telefonosFiltrados.length > 0) formDataToSend.append('telefonos', JSON.stringify(telefonosFiltrados));
            if (correosFiltrados.length > 0) formDataToSend.append('correos', JSON.stringify(correosFiltrados));
            if (formData.locacion.trim()) formDataToSend.append('locacion', formData.locacion.trim());

            await realizarPeticion(`${process.env.REACT_APP_API_URL}/admin/actualizar-plantel/${plantelId}`, {
                method: 'POST',
                headers: {
                    'Authorization':  `Bearer ${token}`
                },
                body: formDataToSend,
            });

            await Swal.fire({
                title: 'Plantel actualizado correctamente.',
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });

            if (onPlantelEditado) { 
                await onPlantelEditado(); 
            }

            handleCloseModal();

        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'Ha ocurrido un error al actualizar el plantel.',
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: { popup: 'popupbonito' }
            });
        }
    }, [formData, plantelId, validators, realizarPeticion, handleCloseModal]);

    const phoneInputProps = useMemo(() => ({
        country: 'mx',
        inputClass: styles.inputTelefono,
        containerClass: styles.phoneInputContainer,
        placeholder: "Ingresa un teléfono de contacto",
        preferredCountries: ['mx', 'us', 'ca'],
        enableSearch: true,
        searchPlaceholder: "Buscar país...",
        disabled: loading
    }), [loading]);

    if (!abrirModal && !isClosing) return null;

    return (
        <div className={styles.modalAniadir} onClick={handleOverlayClick}>
            <div className={`${styles.modalAniadirCont} ${abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''}`}>
                <div className={styles.modalScrollableAniadir}>
                    <button onClick={handleCloseModal} type="button" className={styles.closeBtnAniadir}>
                        ×
                    </button>
                    <h2 className={styles.tituloAniadir}>Editar Plantel</h2>

                    {loading ? (
                        <div className={styles.loading}>
                            <div className={styles.loadingSpinner}></div>
                            <p>Cargando datos del plantel...</p>
                        </div>
                    ) : (
                        <form className={styles.formUsuario} onSubmit={handleSubmit}>
                            <label htmlFor="direccion">
                                Dirección:
                                <small className={styles.helpText}>
                                    Incluye calle, número, colonia y ciudad. Ejemplo: "Av. Universidad 123, Col. Centro, Guadalajara"
                                </small>
                                <input
                                    type="text"
                                    id="direccion"
                                    value={formData.direccion}
                                    placeholder="Ej: Av. Universidad 123, Col. Centro, Santiago de Querétaro, Qro."
                                    onChange={handleDireccionChange}
                                    required
                                    disabled={loading}
                                    minLength="10"
                                    maxLength="200"
                                />
                                {errores.direccion && <p className={styles.mensajeErrorAniadir}>{errores.direccion}</p>}
                            </label>

                            <div className={styles.seccionTelefonos}>
                                <label>Números de teléfono (opcional):</label>
                                {formData.telefonos.map((telefono, index) => (
                                    <div key={index} >
                                        <div className={styles.inputGroup}>
                                        <div className={styles.inputWrapper}>
                                            <PhoneInput
                                                {...phoneInputProps}
                                                value={telefono}
                                                onChange={(value) => handleTelefonoChange(index, value)}
                                                inputProps={{
                                                    'aria-label': `Teléfono de contacto ${index + 1}`,
                                                }}
                                            />
                                            {formData.telefonos.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => eliminarTelefono(index)}
                                                    className={styles.botonEliminar}
                                                    disabled={loading}
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                        </div>
                                        {errores.telefonos[index] && (
                                            <p className={styles.mensajeErrorAniadir}>{errores.telefonos[index]}</p>
                                        )}
                                        </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={agregarTelefono}
                                    className={styles.botonAgregar}
                                    disabled={loading}
                                >
                                    + Agregar teléfono
                                </button>
                            </div>

                            <div className={styles.seccionCorreos}>
                                <label>Correos de contacto (opcional):</label>
                                {formData.correos.map((correo, index) => (
                                    <div key={index}>
                                        <div className={styles.inputGroup}>
                                        <div className={styles.inputWrapper}>
                                            <input
                                                type="email"
                                                value={correo}
                                                placeholder="Ej: contacto@plantel.edu.mx"
                                                onChange={(e) => handleCorreoChange(index, e.target.value)}
                                                className={styles['text-input']}
                                                disabled={loading}
                                            />
                                            {formData.correos.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => eliminarCorreo(index)}
                                                    className={styles.botonEliminar}
                                                    disabled={loading}
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                        </div>
                                        {errores.correos[index] && (
                                            <p className={styles.mensajeErrorAniadir}>{errores.correos[index]}</p>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={agregarCorreo}
                                    className={styles.botonAgregar}
                                    disabled={loading}
                                >
                                    + Agregar correo
                                </button>
                            </div>

                            <label htmlFor="locacion">
                                URL de Google Maps Embed (opcional):
                                <small className={styles.helpText}>
                                    Para obtener la URL: Ve a Google Maps → Busca la ubicación → Compartir → Insertar un mapa → Copia la URL del src
                                </small>
                                <input
                                    type="url"
                                    id="locacion"
                                    value={formData.locacion}
                                    placeholder="Ej: https://www.google.com/maps/embed?pb=..."
                                    onChange={handleLocacionChange}
                                    disabled={loading}
                                />
                                {errores.locacion && <p className={styles.mensajeErrorAniadir}>{errores.locacion}</p>}
                            </label>

                            <button type="submit" className={styles.botonGuard} disabled={loading}>
                                {loading ? 'Actualizando...' : 'Actualizar Plantel'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditarPlantel;